import { useEffect, useState } from "react";
import type { MediaAsset } from "../domain/types";
import { formatBytes } from "../media/createMediaAsset";
import { reviewQueueStore } from "../review/queueStore";

const PREVIEW_FETCH_MS = 12_000;

function hasMediaTicket(url: string): boolean {
  return /[?&]ticket=/.test(url);
}

async function fetchWithTimeout(
  url: string,
  init: RequestInit,
  ms: number
): Promise<Response> {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), ms);
  try {
    return await fetch(url, { ...init, signal: ctrl.signal });
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Private R2 media → blob URL for <img>/<video>.
 * Prefer credentialed fetch (moderator cookie). Ticket URLs also work without
 * cookies after server ACL treats a verified ticket as a capability.
 */
async function hydratePrivateUrl(url: string): Promise<string> {
  if (!url.includes("/api/media/")) return url;
  try {
    const res = await fetchWithTimeout(url, { credentials: "include" }, PREVIEW_FETCH_MS);
    if (!res.ok) {
      // Ticket-only retry (no cookie) — same URL; covers img-style capability fetch.
      if (hasMediaTicket(url) && res.status === 401) {
        const again = await fetchWithTimeout(url, { credentials: "omit" }, PREVIEW_FETCH_MS);
        if (!again.ok) return "";
        const blob = await again.blob();
        if (!blob.size) return "";
        return URL.createObjectURL(blob);
      }
      return "";
    }
    const blob = await res.blob();
    if (!blob.size) return "";
    return URL.createObjectURL(blob);
  } catch {
    // Last resort: let the element request the ticket URL directly.
    return hasMediaTicket(url) ? url : "";
  }
}

export function MediaPreview({
  asset,
  label,
  large,
}: {
  asset: MediaAsset | null;
  label: string;
  large?: boolean;
}) {
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(Boolean(asset));
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!asset) {
      setUrl("");
      setFailed(false);
      setLoading(false);
      return;
    }
    let cancelled = false;
    let objectUrlToRevoke: string | null = null;

    const run = async () => {
      setFailed(false);
      setLoading(true);
      setUrl("");

      try {
        const sync = reviewQueueStore.resolvePreviewUrl(asset);
        let resolved = sync;
        if (!resolved) {
          resolved = await Promise.race([
            reviewQueueStore.ensurePreview(asset),
            new Promise<string>((resolve) => setTimeout(() => resolve(""), PREVIEW_FETCH_MS)),
          ]);
        }
        if (cancelled) return;
        if (!resolved) {
          setUrl("");
          setFailed(true);
          return;
        }

        const hydrated = await hydratePrivateUrl(resolved);
        if (cancelled) return;
        if (hydrated.startsWith("blob:")) {
          objectUrlToRevoke = hydrated;
          setUrl(hydrated);
          setFailed(false);
        } else if (hydrated) {
          // Ticket URL fallback for direct element load
          setUrl(hydrated);
          setFailed(false);
        } else {
          setUrl("");
          setFailed(true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void run();
    return () => {
      cancelled = true;
      if (objectUrlToRevoke) URL.revokeObjectURL(objectUrlToRevoke);
    };
  }, [asset?.id, asset?.storageKey, asset?.publicUrl, asset?.objectUrl]);

  if (!asset) {
    return (
      <div className={`rc-media${large ? " rc-media--large" : ""} is-missing`}>
        <div className="rc-media__label">{label}</div>
        <p className="rc-media__empty">Not uploaded</p>
      </div>
    );
  }

  const isPdf = asset.mimeType === "application/pdf" || /\.pdf$/i.test(asset.fileName);
  const isVideo =
    asset.mimeType.startsWith("video/") ||
    asset.kind === "confirmation_video" ||
    /\.(webm|mp4|mov)$/i.test(asset.fileName);
  const isImage =
    asset.mimeType.startsWith("image/") ||
    asset.kind === "government_id" ||
    asset.kind === "profile_photo" ||
    /\.(jpe?g|png|webp|gif)$/i.test(asset.fileName);

  return (
    <div className={`rc-media${large ? " rc-media--large" : ""}`}>
      <div className="rc-media__head">
        <div className="rc-media__label">{label}</div>
        <span className={`rc-badge rc-badge--${asset.reviewStatus}`}>
          {asset.reviewStatus}
        </span>
      </div>

      <div className="rc-media__frame">
        {url && isImage && !isVideo ? (
          <img
            src={url}
            alt={label}
            className="rc-media__img"
            onError={() => {
              setFailed(true);
              setUrl("");
            }}
          />
        ) : null}
        {url && isVideo ? (
          <video
            src={url}
            className="rc-media__video"
            controls
            playsInline
            preload="metadata"
            onError={() => {
              setFailed(true);
              setUrl("");
            }}
          />
        ) : null}
        {isPdf ? (
          <div className="rc-media__placeholder">
            <strong>PDF</strong>
            <span>{asset.fileName}</span>
            {url ? (
              <a href={url} target="_blank" rel="noreferrer" className="rc-media__link">
                Open document
              </a>
            ) : null}
          </div>
        ) : null}
        {!url && !isPdf ? (
          <div className="rc-media__placeholder">
            <strong>
              {loading
                ? "Loading preview…"
                : failed
                  ? "Preview unavailable"
                  : "No live preview"}
            </strong>
            <span>
              {loading
                ? "Resolving from durable storage…"
                : failed
                  ? "Could not fetch private media (sign in as owner or moderator)."
                  : asset.storageKey
                    ? "Waiting for storage resolve…"
                    : "No storageKey — capture again to persist this file."}
            </span>
            <span className="rc-media__meta-line">
              {asset.fileName} · {formatBytes(asset.sizeBytes)} · {asset.mimeType || "unknown"}
            </span>
          </div>
        ) : null}
      </div>

      <div className="rc-media__meta">
        {asset.fileName} · {formatBytes(asset.sizeBytes)}
        {asset.uploadStatus ? ` · ${asset.uploadStatus}` : ""}
        {asset.publicUrl ? ` · view` : ""}
        {asset.storageKey ? ` · ${asset.storageKey}` : ""}
      </div>
    </div>
  );
}
